import { KEM_BACKEND_API_URL } from "@ch/api";
import { filterNullishFromObject } from "@ch/helpers/utils";
import { NotifyError } from "@ch/helpers/utils/helpers";
import type { TKemMessagePayload, BotChatProps } from "./types";

interface TResponse {
  status: "success";
  message: string;
}

const getPropsToSend = (props: TKemMessagePayload): BotChatProps => {
  const propsToSend: BotChatProps = {
    ...props,
    exercise_duration_last_30_days: props.exercise_duration_last_30_days
      ? Number(props.exercise_duration_last_30_days)
      : undefined,
    latest_bmi: props.latest_bmi ? Number(props.latest_bmi) : undefined,

    // These fields are not needed to be sent to the backend
    user_picture: undefined,
    user_full_name: undefined,
    resource_url: undefined,
    seen: undefined,
    health_coach_name: undefined,
    location: undefined,
    action_plan: undefined,
    summarized_notes: undefined,
    message: undefined, // audio endpoint doesn't need message text
  };

  return filterNullishFromObject(propsToSend) as BotChatProps;
};

interface TPostChatAudio {
  messageProps: TKemMessagePayload;
  audioFile: Blob;
  audioFileName?: string;
  onResponseComplete?: (response: {
    message?: string;
    audioUrl: string;
  }) => void;
  handleLoading?: (isLoading: boolean) => void;
  refreshToken?: string | null;
}

const postChatAudio = async ({
  messageProps,
  audioFile,
  audioFileName = "recording.webm",
  onResponseComplete,
  handleLoading,
  refreshToken,
}: TPostChatAudio): Promise<TResponse | undefined> => {
  const propsToSend = getPropsToSend(messageProps);

  handleLoading?.(true);

  try {
    const formData = new FormData();

    formData.append("file", audioFile, audioFileName);

    formData.append("data", JSON.stringify(propsToSend));

    const response = await fetch(`${KEM_BACKEND_API_URL}/speech-to-speech`, {
      method: "POST",
      headers: {
        accept: "application/json",
        // don't set Content-Type for FormData - browser sets it automatically with boundary
        "X-Token": refreshToken ?? "",
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = (await response.json()) as TResponse;

    onResponseComplete?.({
      audioUrl: result?.message || "",
      message: result.message || "",
    });

    return result;
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error";

    NotifyError(errorMessage);

    throw error;
  } finally {
    handleLoading?.(false);
  }
};

export default postChatAudio;
